<script lang="ts">
	import { t, translator } from '#lib/i18n/tranlsator.svelte.ts';
	import type { Component } from 'svelte';
	import IconCode from 'virtual:icons/material-symbols/data-object-rounded';
	import IconDocument from 'virtual:icons/material-symbols/description-outline-rounded';
	import IconImage from 'virtual:icons/material-symbols/image-outline-rounded';
	import IconSlides from 'virtual:icons/material-symbols/slideshow-outline-rounded';
	import IconTable from 'virtual:icons/material-symbols/table-outline-rounded';

	interface Props {
		name: string;
		/** In bytes. Shown when there's no page count. */
		size?: number;
		pageCount?: number;
		/** Shown instead of the type and size, and turns the chip red. */
		error?: string | null;
		uploading?: boolean;
		/** Makes the chip a link to the file. */
		href?: string;
	}

	const { name, size, pageCount = 0, error, uploading = false, href }: Props = $props();

	interface FileType {
		icon: Component;
		/** Proprietary formats get their app's brand color. Open ones like PDF stay neutral. */
		brandColor?: string;
	}

	/** By extension. Text and Markdown files get the document icon. The brand
	 *  colors are the main tile colors of Microsoft's Office icons (2019 design). */
	const FILE_TYPES: Record<string, FileType> = {
		docx: { icon: IconDocument, brandColor: '#185abd' },
		xlsx: { icon: IconTable, brandColor: '#107c41' },
		pptx: { icon: IconSlides, brandColor: '#c43e1c' },
		pdf: { icon: IconDocument },
		csv: { icon: IconTable },
		json: { icon: IconCode },
		xml: { icon: IconCode },
		// Only in the message input, for an image that failed to upload.
		png: { icon: IconImage },
		jpg: { icon: IconImage },
		jpeg: { icon: IconImage },
		webp: { icon: IconImage },
		gif: { icon: IconImage },
		svg: { icon: IconImage }
	};

	const SIZE_UNITS = ['byte', 'kilobyte', 'megabyte'];

	/** 1024-based, like the size limit in the upload errors. */
	function formatSize(bytes: number): string {
		const exponent = bytes < 1024 ? 0 : bytes < 1024 ** 2 ? 1 : 2;
		return new Intl.NumberFormat(translator.locale, {
			style: 'unit',
			unit: SIZE_UNITS[exponent],
			maximumFractionDigits: 1
		}).format(bytes / 1024 ** exponent);
	}

	const extension = $derived(name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');
	const type: FileType = $derived(FILE_TYPES[extension] ?? { icon: IconDocument });
	const Icon = $derived(type.icon);

	const meta = $derived.by(() => {
		if (error) {
			return error;
		}
		const amount =
			pageCount > 1
				? t('chat.attachment.pages', { count: pageCount })
				: size !== undefined
					? formatSize(size)
					: '';
		return [extension.toUpperCase(), amount].filter(Boolean).join(' · ');
	});
</script>

<svelte:element
	this={href ? 'a' : 'div'}
	class="file-chip"
	class:error={!!error}
	{href}
	target={href && '_blank'}
	rel={href && 'noopener noreferrer'}
	title={name}
>
	<div
		class="icon"
		class:brand={!!type.brandColor}
		class:uploading
		style:background-color={type.brandColor}
	>
		<Icon />
	</div>
	<div class="text">
		<span class="name">{name}</span>
		{#if meta}
			<span class="meta">{meta}</span>
		{/if}
	</div>
</svelte:element>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.file-chip {
		@include lib.flex-row();
		@include lib.flex-align(flex-start, center);
		gap: 0.5rem;
		width: 12rem;
		max-width: 100%;
		height: 3rem;
		padding: 0 0.75rem 0 0.5rem;
		border: 1px solid var(--border-subtle);
		border-radius: 0.5rem;
		background: var(--surface-overlay-subtle);
		color: var(--text-primary);
		text-decoration: none;

		&.error {
			border-color: var(--border-error);
			background: var(--surface-error-subtle);

			.meta {
				color: var(--text-error);
			}
		}

		.icon {
			@include lib.flex-row();
			@include lib.flex-align(center, center);
			flex-shrink: 0;
			width: 2rem;
			height: 2rem;
			border-radius: 0.25rem;
			background: var(--surface-2);
			font-size: 1.2rem;
			color: var(--text-secondary);

			// On the brand color in both themes.
			&.brand {
				color: white;
			}

			&.uploading {
				@include lib.skeleton-pulse;
			}
		}

		.text {
			@include lib.flex-column();
			min-width: 0;
			font-size: 0.875rem;
			// Not the chat message's, so it looks the same there as in the input.
			line-height: normal;

			.name,
			.meta {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.meta {
				font-size: 0.75rem;
				color: var(--text-secondary);
			}
		}
	}
</style>
